import { useDispatch, useSelector } from 'react-redux';

import { AccountTierType } from '@/proxies/UserProxy';
import { isNil } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  setAccessToken,
  setLoginExpiration,
  setUserId,
} from '../redux/ducks/StorageDucks';
import { setUserTopMenuOpen } from '../redux/ducks/UIDucks';
import type { ProfileResponseDataType } from '../redux/ducks/UserDucks.types';
import type { RootReducerType } from '../redux/reducers/RootReducer.types';

interface UserStore {
  isLoggedIn: boolean;
  isPartner: boolean;
  isApprovedPartner: boolean;
  currentUser: ProfileResponseDataType | null;
  logout: () => void;
  onTopMenuToggle: (open: boolean) => void;
  isTopMenuOpen: boolean;
  hasReachedActiveLimit: boolean;
  hasReachedMonthlyLimit: boolean;
  hasReachedContactsLimit: boolean;
  hasReachedBusinessesLimit: boolean;
  hasReachedServicesLimit: boolean;
  userCurrency?: string;
}

const useUser = (): UserStore => {
  const [onLogout, setOnLogout] = useState<boolean>(false);
  const userId = useSelector((state: RootReducerType) => state.storage.userId);
  const userProfile = useSelector(
    (state: RootReducerType) => state.user.profileResponse,
  );
  const { getPartnerDetailsResponse, getPartnerContactsResponse } = useSelector(
    (state: RootReducerType) => state.dashboard,
  );
  const { loginExpires: loginExpiration, accessToken } = useSelector(
    (state: RootReducerType) => state.storage,
  );
  const { appConfig } = useSelector((state: RootReducerType) => state.config);
  const { isUserTopMenuOpen: isTopMenuOpen } = useSelector(
    (state: RootReducerType) => state.ui,
  );

  const dispatch = useDispatch();

  const isLoggedIn = useMemo<boolean>(
    () =>
      !isNil(userId) && !isNil(loginExpiration) && loginExpiration > Date.now(),
    [userId, loginExpiration],
  );

  const currentUser = useMemo<ProfileResponseDataType | null>(
    () =>
      !isNil(userId) &&
      !isNil(loginExpiration) &&
      loginExpiration > Date.now() &&
      !isNil(userProfile) &&
      userProfile?.data?.id === userId
        ? userProfile.data
        : null,
    [userId, loginExpiration, userProfile],
  );

  const isPartner = useMemo<boolean>(
    () =>
      !isNil(userProfile) &&
      !isNil(userProfile.data?.partner) &&
      userProfile.data?.partner === true,
    [userProfile],
  );

  const isApprovedPartner = useMemo<boolean>(
    () =>
      !isNil(userProfile) &&
      !isNil(userProfile.data?.partner) &&
      userProfile.data?.partner === true &&
      userProfile.data?.approved === true,
    [userProfile],
  );

  const hasReachedActiveLimit = useMemo<boolean>(() => {
    let result = false;
    if (
      !isNil(userProfile?.data?.partner_id) &&
      !isNil(getPartnerDetailsResponse?.data) &&
      !isNil(appConfig)
    ) {
      if (userProfile.data.partner_id === getPartnerDetailsResponse?.data.id) {
        const accountTier = userProfile.data.account_tier;
        if (
          accountTier === AccountTierType.Free ||
          accountTier === AccountTierType.Unknown
        ) {
          if (isNil(appConfig?.free_tier_max_appointments_active)) {
            return true;
          }

          const maxActiveFree = appConfig.free_tier_max_appointments_active;
          const nextYearAppointments =
            getPartnerDetailsResponse.data.next_year_active_appointments;

          let totalActive = 0;
          for (let index = 0; index < nextYearAppointments.length; index++) {
            const monthAmount = nextYearAppointments[index];
            totalActive += monthAmount;
          }

          result = maxActiveFree <= totalActive;
        } else if (accountTier === AccountTierType.Pro) {
          if (isNil(appConfig?.pro_tier_max_appointments_active)) {
            return true;
          }

          const maxActivePro = appConfig.pro_tier_max_appointments_active;
          const nextYearAppointments =
            getPartnerDetailsResponse.data.next_year_active_appointments;

          let totalActive = 0;
          for (let index = 0; index < nextYearAppointments.length; index++) {
            const monthAmount = nextYearAppointments[index];
            totalActive += monthAmount;
          }

          result = maxActivePro <= totalActive;
        }
      }
    }

    return result;
  }, [userProfile, getPartnerDetailsResponse, appConfig]);

  const hasReachedMonthlyLimit = useMemo<boolean>(() => {
    let result = false;
    if (
      !isNil(userProfile?.data?.partner_id) &&
      !isNil(getPartnerDetailsResponse?.data) &&
      !isNil(appConfig)
    ) {
      if (userProfile.data.partner_id === getPartnerDetailsResponse?.data.id) {
        const accountTier = userProfile.data.account_tier;

        if (
          accountTier === AccountTierType.Free ||
          accountTier === AccountTierType.Unknown
        ) {
          if (isNil(appConfig?.free_tier_max_appointments_per_month)) {
            return true;
          }

          const maxMonthyFree = appConfig.free_tier_max_appointments_per_month;
          const nextYearAppointments =
            getPartnerDetailsResponse.data.next_year_active_appointments;

          let maxFreeMonth = 0;
          for (let index = 0; index < nextYearAppointments.length; index++) {
            const monthAmount = nextYearAppointments[index];
            maxFreeMonth = Math.max(monthAmount, maxFreeMonth);
          }

          result = maxMonthyFree <= maxFreeMonth;
        } else if (accountTier === AccountTierType.Pro) {
          if (isNil(appConfig?.pro_tier_max_appointments_per_month)) {
            return true;
          }

          const maxMonthyPro = appConfig.pro_tier_max_appointments_per_month;
          const nextYearAppointments =
            getPartnerDetailsResponse.data.next_year_active_appointments;

          let maxProMonth = 0;
          for (let index = 0; index < nextYearAppointments.length; index++) {
            const monthAmount = nextYearAppointments[index];
            maxProMonth = Math.max(monthAmount, maxProMonth);
          }

          result = maxMonthyPro <= maxProMonth;
        }
      }
    }

    return result;
  }, [userProfile, getPartnerDetailsResponse, appConfig]);

  const hasReachedContactsLimit = useMemo<boolean>(() => {
    let result = false;

    if (
      !isNil(userProfile?.data?.partner_id) &&
      !isNil(getPartnerContactsResponse?.data) &&
      !isNil(appConfig)
    ) {
      if (
        userProfile.data.partner_id === getPartnerContactsResponse?.data.partner
      ) {
        const accountTier = userProfile.data.account_tier;
        if (
          accountTier === AccountTierType.Free ||
          accountTier === AccountTierType.Unknown
        ) {
          if (isNil(appConfig?.free_tier_max_contacts)) {
            result = true;
          } else {
            result =
              getPartnerContactsResponse.data.contacts.length >=
              appConfig.free_tier_max_contacts;
          }
        }
      }
    }

    return result;
  }, [userProfile, getPartnerContactsResponse, appConfig]);

  const hasReachedBusinessesLimit = useMemo<boolean>(() => {
    let result = false;

    if (
      !isNil(userProfile?.data?.partner_id) &&
      !isNil(getPartnerDetailsResponse?.data) &&
      !isNil(appConfig)
    ) {
      if (userProfile.data.partner_id === getPartnerDetailsResponse?.data.id) {
        const accountTier = userProfile.data.account_tier;
        if (
          accountTier === AccountTierType.Free ||
          accountTier === AccountTierType.Unknown
        ) {
          if (isNil(appConfig?.free_tier_max_businesses)) {
            result = true;
          } else {
            result =
              getPartnerDetailsResponse.data.businesses.length >=
              appConfig.free_tier_max_businesses;
          }
        }
      }
    }

    return result;
  }, [getPartnerDetailsResponse, appConfig, userProfile]);

  const hasReachedServicesLimit = useMemo<boolean>(() => {
    let result = false;

    if (
      !isNil(userProfile?.data?.partner_id) &&
      !isNil(getPartnerDetailsResponse?.data) &&
      !isNil(appConfig)
    ) {
      if (userProfile.data.partner_id === getPartnerDetailsResponse?.data.id) {
        const accountTier = userProfile.data.account_tier;
        if (
          accountTier === AccountTierType.Free ||
          accountTier === AccountTierType.Unknown
        ) {
          if (isNil(appConfig?.free_tier_max_services)) {
            result = true;
          } else {
            result =
              getPartnerDetailsResponse.data.services.length >=
              appConfig.free_tier_max_services;
          }
        }
      }
    }

    return result;
  }, [getPartnerDetailsResponse, appConfig, userProfile]);

  const userCurrency = useMemo<string | undefined>(() => {
    return undefined; // TODO
  }, []);

  const logout = useCallback(() => {
    setOnLogout(true);
    dispatch(setLoginExpiration(null));
    dispatch(setUserId(null));
    dispatch(setAccessToken(null));
  }, [dispatch]);

  useEffect(() => {
    if (typeof window !== 'undefined' && onLogout) {
      if (isNil(loginExpiration) && isNil(userId) && isNil(accessToken)) {
        setOnLogout(false);
        window.location.reload();
      }
    }
  }, [onLogout, loginExpiration, userId, accessToken]);

  useEffect(() => {
    if (!isNil(userId) && !isNil(userProfile) && !isNil(userProfile.data?.id)) {
      if (userProfile.data.id !== userId) {
        logout();
      }
    }
  }, [userId, userProfile, logout]);

  const onTopMenuToggle = useCallback(
    (open: boolean) => {
      dispatch(setUserTopMenuOpen(open));
    },
    [dispatch],
  );

  return {
    isLoggedIn,
    currentUser,
    isPartner,
    isApprovedPartner,
    logout,
    onTopMenuToggle,
    isTopMenuOpen,
    hasReachedActiveLimit,
    hasReachedMonthlyLimit,
    hasReachedContactsLimit,
    hasReachedBusinessesLimit,
    hasReachedServicesLimit,
    userCurrency,
  };
};

export default useUser;
